<template>
	<div class="relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]">

		<!-- Header -->
		<div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
			<div>
				<h1
				class="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:secondary">
				Employees
				</h1>
				<p class="text-sm text-slate-500 dark:text-slate-200 mt-2">
				Manage and monitor all employee accounts
				</p>
			</div>
			<div class="flex items-center gap-2">
				<button @click="showStats = !showStats"
					class="px-4 py-2 rounded-lg font-semibold transition border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center justify-center w-full sm:w-auto">
					<Eye v-if="!showStats" class="h-5 w-5 mr-2" />
					<EyeOff v-else class="h-5 w-5 mr-2" />
					<span class="hidden sm:inline">{{ showStats ? 'Hide Stats' : 'Show Stats' }}</span>
				</button>
				<button @click="openCreateModal"
					class="
					px-6 py-2 rounded-lg font-semibold text-white transition
					bg-primary hover:bg-secondary
					shadow-md hover:opacity-90
					dark:bg-gradient-to-tr dark:from-primary/80 dark:to-secondary/80
					dark:shadow-none dark:ring-1 dark:ring-white/10
					inline-flex items-center justify-center w-full sm:w-auto
					">
					<UserRoundPlus class="h-5 w-5 mr-2" />
					<span class="hidden sm:inline">Add New Employee</span>
				</button>
			</div>
		</div>

		<!-- STATS BAR (collapsible) -->
		<Transition name="stats">
			<div v-if="showStats" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
				<StatCard
					label="Total Employees"
					:value="stats.total"
					:loading="loading"
					:icon="Users"
					color=" text-blue-600  dark:text-blue-400"
				/>
				<StatCard
					label="Active"
					:value="stats.active"
					:loading="loading"
					:icon="UserCheck"
					color="text-emerald-600 dark:text-emerald-400"
				/>
				<StatCard
					label="Inactive"
					:value="stats.inactive"
					:loading="loading"
					:icon="UserX"
					color="text-rose-600 dark:text-rose-400"
				/>
				<StatCard
					label="Pending"
					:value="stats.pending"
					:loading="loading"
					:icon="Clock"
					color="text-amber-600 dark:text-amber-400"
				/>
			</div>
		</Transition>

		<!-- Search and Filters -->
		<TableControls
			v-model:search="searchQuery"
			v-model:status="statusFilter"
			v-model:perPage="perPage"
			:options="statusOptions"
			filterLabel="Status"
			placeholder="Search by name, username or email..."
		/>

		<!-- Error State -->
		<div v-if="error" 
			class="card border border-danger/20 dark:border-danger-dark/20 bg-danger/5 dark:bg-danger-dark/5 shadow-sm"
		>
			<div class="card-body">
				<div class="flex items-start space-x-3">
					<div class="flex-shrink-0">
						<div
						class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-danger/10 dark:bg-danger-dark/10">
						<AlertCircle class="h-5 w-5 text-danger dark:text-danger-dark" />
						</div>
					</div>
					<div class="flex-1">
						<h3 class="text-sm font-semibold text-danger dark:text-danger-dark mb-1">
						Error Loading Employees
						</h3>
						<p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
						{{ error }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Employee Table -->
		<EmployeeTable
		v-if="!error"
		:employees="paginatedEmployees"
		:loading="loading"
		@view="handleView"
		@activate="handleActivate"
		@approve="handleApprove"
		@deactivate="handleDeactivate"
		@delete="handleDelete"
		/>

		<!-- Pagination -->
		<div
			v-if="!loading && !error && filteredTotal > 0"
			class="card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
			>
			<div class="p-4">
				<PaginationBar
				:current-page="currentPage"
				:total-pages="filteredTotalPages"
				:total="filteredTotal"
				:per-page="perPage"
				@update:page="goToPage"
				/>
			</div>
		</div>

		<!-- Details Modal -->
		<EmployeeDetailsModal
			:is-open="showDetailsModal"
			:employee="selectedEmployeeForDetails"
			:loading="detailsLoading"
			@close="closeDetailsModal"
			@edit="handleEditFromDetails"
		/>

		<!-- View/Edit Modal -->
		<EmployeeModal
		:is-open="showModal || showCreateModal"
		:employee="selectedEmployee"
		:edit-mode="editMode"
		:validation-errors="createValidationErrors"
		:existing-employees="employees"
		:positions="positions"
		:loading="loading"
		@close="closeModalOrCreateModal"
		@save="handleSave"
		@update="handleUpdate"
		@edit="handleEdit" />

		<!-- Delete Confirmation Modal -->
		<Modal v-model="showDeleteConfirm" title="Delete Employee">
			<p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
				Are you sure you want to permanently delete
				<span class="font-semibold text-slate-700 dark:text-slate-300">"{{ pendingDeleteName }}"</span>?
				This action cannot be undone.
			</p>

			<template #footer>
				<button type="button"
				class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
				@click="cancelDelete">
				Cancel
				</button>
				<button type="button"
				class="ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
				@click="confirmDelete">
				Yes, delete permanently
				</button>
			</template>
		</Modal>
		<!-- Approve/Activate Confirmation Modal -->
		<Modal v-model="showApproveConfirm" :title="confirmActionType === 'approve' ? 'Approve Employee' : 'Activate Employee'">
			<p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
				Are you sure you want to {{ confirmActionType === 'approve' ? 'approve and activate' : 'reactivate' }}
				<span class="font-semibold text-slate-700 dark:text-slate-300">"{{ pendingApproveName }}"</span>?
			</p>

			<template #footer>
				<button type="button"
				class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
				@click="cancelApprove">
				Cancel
				</button>
				<button type="button"
				class="flex justify-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-success hover:bg-success/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
				@click="confirmApprove" :disabled="loading">
				<LoaderCircle v-if="loading" class="w-5 h-5 mr-2 animate-spin" />
				{{ loading ? 'Processing...' : `Yes, ${confirmActionType === 'approve' ? 'approve and activate' : 'reactivate'}` }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useToast } from 'vue-toastification'
import { useEmployees } from '@/composables/useEmployees'
import TableControls from '@/components/layout/TableControls.vue'
import EmployeeTable from '@/components/employees/EmployeeTable.vue'
import EmployeeModal from '@/components/employees/EmployeeModal.vue'
import EmployeeDetailsModal from '@/components/employees/EmployeeDetailsModal.vue'
import Modal from '@/components/layout/Modal.vue'
import StatCard from '@/components/layout/StatCard.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import type { Employee, CreateEmployeeData } from '@/services/employeeService'
import { employeeService } from '@/services/employeeService'
import { positionService, type Position } from '@/services/positionService'
import { UserRoundPlus, AlertCircle, Users, UserCheck, UserX, Clock, Eye, EyeOff, LoaderCircle } from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')
useTitle(`${appTitle.value} - Employees`)

const toast = useToast()
const toastTimeout = 5000

const {
	employees,
	loading,
	error,
	stats,
	fetchEmployees,
	createEmployee,
	updateEmployee,
	toggleEmployeeStatus,
	deleteEmployee,
	clearError
} = useEmployees()

const showStats = ref(false)
const showDetailsModal = ref(false)
const detailsLoading = ref(false)
const selectedEmployeeForDetails = ref<Employee | null>(null)
const showModal = ref(false)
const showCreateModal = ref(false)
const createValidationErrors = ref<Record<string, string>>({})
const selectedEmployee = ref<Employee | null>(null)
const editMode = ref(false)
const positions = ref<Position[]>([])
const showDeleteConfirm = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteName = computed(() => {
  if (pendingDeleteId.value === null) return 'this employee'

  const employee = employees.value.find((item) => item.id === pendingDeleteId.value)
  if (!employee) return 'this employee'

  if (employee.first_name && employee.last_name) {
    return `${employee.first_name} ${employee.last_name}`
  }

  return employee.name || employee.username || 'this employee'
})

const confirmActionType = ref<'approve' | 'activate'>('approve')
const showApproveConfirm = ref(false)
const pendingApproveId = ref<number | null>(null)
const pendingApproveName = computed(() => {
  if (pendingApproveId.value === null) return 'this employee'

  const employee = employees.value.find((item) => item.id === pendingApproveId.value)
  if (!employee) return 'this employee'

  if (employee.first_name && employee.last_name) {
    return `${employee.first_name} ${employee.last_name}`
  }

  return employee.name || employee.username || 'this employee'
})

const statusOptions = [
	{ value: 'all', label: 'All Status' },
	{ value: 'active', label: 'Active' },
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'pending', label: 'Pending' }
]

// Status filter (client-side)
const statusFilter = ref<'all' | 'active' | 'inactive' | 'pending'>('all')
const statusFiltered = computed(() => {
	const list = employees.value || []
	if (statusFilter.value === 'active') return list.filter((e) => e.status === 'active')
	if (statusFilter.value === 'inactive') return list.filter((e) => e.status === 'inactive')
	if (statusFilter.value === 'pending') return list.filter((e) => e.status === 'pending')
	return list
})

// Search filter (client-side) - searches across multiple fields
const searchQuery = ref('')
const searchFiltered = computed(() => {
	const list = statusFiltered.value
	if (!searchQuery.value.trim()) return list
	
	const query = searchQuery.value.toLowerCase()
	return list.filter((e) => {
		const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase()
		const username = (e.username || '').toLowerCase()
		const email = (e.email || '').toLowerCase()
		return name.includes(query) || username.includes(query) || email.includes(query)
	})
})

// Pagination (client-side)
const perPage = ref(10)
const currentPage = ref(1)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))
const filteredTotal = computed(() => searchFiltered.value.length)

const paginatedEmployees = computed(() => {
	const start = (currentPage.value - 1) * perPage.value
	const end = start + perPage.value
	return searchFiltered.value.slice(start, end)
})

// Reset page when filters change
watch([statusFilter, searchQuery, perPage], () => {
  	currentPage.value = 1
})

const goToPage = (page: number) => {
  	currentPage.value = page
}

const handleView = async (id: number) => {
	try {
		detailsLoading.value = true
		showDetailsModal.value = true
		const employee = await employeeService.getEmployee(id)
		selectedEmployeeForDetails.value = employee
	} catch (err) {
		console.error('Failed to fetch employee:', err)
		showDetailsModal.value = false
	} finally {
		detailsLoading.value = false
	}
}

const closeDetailsModal = () => {
	showDetailsModal.value = false
	selectedEmployeeForDetails.value = null
}

const handleEditFromDetails = () => {
	const employeeToEdit = selectedEmployeeForDetails.value
	closeDetailsModal()
	selectedEmployee.value = employeeToEdit
	editMode.value = true
	showModal.value = true
}

const handleEdit = () => {
  	editMode.value = true
}


const handleApprove = (id: number) => {
	confirmActionType.value = 'approve'
	pendingApproveId.value = id
	showApproveConfirm.value = true
}

const handleActivate = (id: number) => {
	confirmActionType.value = 'activate'
	pendingApproveId.value = id
	showApproveConfirm.value = true
}

const confirmApprove = async () => {
	if (pendingApproveId.value === null) return
	
	const id = pendingApproveId.value
	
	try {
		await toggleEmployeeStatus(id, 'active')
		// Close modal before refresh to prevent rendering conflicts
		closeModalOrCreateModal()
		
		toast.success('Employee activated successfully', { timeout: toastTimeout })
		await fetchEmployees()
	} catch (err: any) {
		const errorMessage = err?.response?.data?.message || 'Failed to activate employee'
		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Failed to activate employee:', err)
	} finally {
		showApproveConfirm.value = false
		pendingApproveId.value = null
	}
}

const cancelApprove = () => {
	showApproveConfirm.value = false
	pendingApproveId.value = null
}

const handleDeactivate = async (id: number) => {
	try {
		await toggleEmployeeStatus(id, 'inactive')
		// Close modal before refresh to prevent rendering conflicts
		closeModalOrCreateModal()
		
		toast.success('Employee deactivated successfully', { timeout: toastTimeout })
		await fetchEmployees()
	} catch (err: any) {
		const errorMessage = err?.response?.data?.message || 'Failed to deactivate employee'
		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Failed to deactivate employee:', err)
	}
}

const handleDelete = (id: number) => {
	pendingDeleteId.value = id
	showDeleteConfirm.value = true
}

const confirmDelete = async () => {
	if (pendingDeleteId.value === null) return

	const id = pendingDeleteId.value

	try {
		await deleteEmployee(id)
		// Close modal before refresh to prevent rendering conflicts
		closeModalOrCreateModal()
		
		toast.success('Employee deleted successfully', { timeout: toastTimeout })
		await fetchEmployees()
	} catch (err: any) {
		const errorMessage = err?.response?.data?.message || 'Failed to delete employee'
		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Failed to delete employee:', err)
	} finally {
		showDeleteConfirm.value = false
		pendingDeleteId.value = null
	}
}

const cancelDelete = () => {
	showDeleteConfirm.value = false
	pendingDeleteId.value = null
}

const closeModalOrCreateModal = () => {
	showModal.value = false
	showCreateModal.value = false
	selectedEmployee.value = null
	editMode.value = false
	createValidationErrors.value = {}
	clearError()
	closeDetailsModal()
}

const openCreateModal = () => {
	selectedEmployee.value = null
	editMode.value = false
	createValidationErrors.value = {}
	clearError()
	showCreateModal.value = true
}

const handleSave = async (data: CreateEmployeeData & { password_confirmation?: string }) => {
	createValidationErrors.value = {}
	clearError()

	try {
		// Map frontend field names to backend field names
		const apiData: any = { ...data }
		if (apiData.street !== undefined) {
			apiData.address = apiData.street
			delete apiData.street
		}
		if (apiData.position_id !== undefined) {
			apiData.position = apiData.position_id
			delete apiData.position_id
		}

		await createEmployee(apiData)

		const employeeName = data.first_name && data.last_name
		? `${data.first_name} ${data.last_name}`
		: 'Employee'
		toast.success(`Employee "${employeeName}" has been created successfully!`, { timeout: toastTimeout })

		await fetchEmployees()
		showCreateModal.value = false
	} catch (error: any) {
		const backendErrors = error?.response?.data?.errors

		if (backendErrors && typeof backendErrors === 'object') {
			const mappedErrors: Record<string, string> = {}

			Object.entries(backendErrors).forEach(([field, value]) => {
				if (Array.isArray(value) && value.length > 0) {
				mappedErrors[field] = String(value[0])
				} else if (value) {
				mappedErrors[field] = String(value)
				}
			})

			createValidationErrors.value = mappedErrors
			clearError()
			return
		}

		const errorMessage = error?.response?.data?.message ||
		error?.response?.data?.error ||
		error?.message ||
		'Failed to create employee. Please try again.'

		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Error creating employee:', error)
	}
}
const handleUpdate = async (id: number, data: any) => {
	try {
		// Validate required fields
		if (!data.first_name || !data.last_name || !data.username || !data.email) {
			toast.error('Please fill in all required fields (First Name, Last Name, Username, Email)', { timeout: toastTimeout })
			return
		}

		// Validate password if provided
		if (data.password) {
			if (data.password.length < 8) {
				toast.error('Password must be at least 8 characters long', { timeout: toastTimeout })
				return
			}

			// Validate password confirmation
			if (data.password_confirmation && data.password !== data.password_confirmation) {
				toast.error('Passwords do not match', { timeout: toastTimeout })
				return
			}
		}

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(data.email)) {
			toast.error('Please enter a valid email address', { timeout: toastTimeout }	)
			return
		}

		// Call the updateEmployee function from the composable
		await updateEmployee(id, data);

		// Show success toast
		const employeeName = data.first_name && data.last_name
		? `${data.first_name} ${data.last_name}`
		: 'Employee'
		toast.success(`Employee "${employeeName}" has been updated successfully!`, { timeout: toastTimeout })

		// Refresh the employee list
		await fetchEmployees();

		// Close the modal and reset
		showModal.value = false
		selectedEmployee.value = null
		editMode.value = false
	} catch (error: any) {
		// Extract error message from API response
		const errorMessage = error?.response?.data?.message ||
		error?.response?.data?.error ||
		error?.message ||
		'Failed to update employee. Please try again.'

		// Show validation errors from backend
		if (error?.response?.data?.errors) {
			const errors = error.response.data.errors
			const firstError = Object.values(errors)[0]
			const errorMsg = Array.isArray(firstError) ? firstError[0] : firstError
			toast.error(errorMsg as string, { timeout: toastTimeout })
		} else {
			toast.error(errorMessage, { timeout: toastTimeout })
		}
		console.error('Error updating employee:', error);
	}
}

onMounted(async () => {
	try {
		positions.value = await positionService.getPositions()
		await fetchEmployees()
	} catch (err) {
		console.error('Failed to load initial data:', err)
	}
})
</script>

<style scoped>
.stats-enter-active,
.stats-leave-active {
	transition: all 0.3s ease;
}
.stats-enter-from,
.stats-leave-to {
	opacity: 0;
	transform: translateY(-10px);
}
</style>
